import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";

import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { companyPermissionModules } from "../../../../utils/Constants";
import {
  extractAdminPortalData,
  useAdminModulePermission,
} from "../../../../utils/adminPortalHelpers";
import { showErrorMsg } from "../../../../utils/Helpers";
import { getAdminCompanyClient } from "../../../../network/apis/adminCompanyClients";
import {
  adminCompanyClientRoutes,
  COMPANY_DETAILS_CLIENTS_TAB,
} from "../adminCompanyClientRoutes";

const useAdminViewClient = () => {
  const history = useHistory();
  const location = useLocation();
  const { companyId, id } = useParams();

  const lang = useSelector((state) => state.locale.lang);
  const { clients, shared } = messages[lang];
  const companiesList = messages[lang].companiesList;
  const { canEdit } = useAdminModulePermission(
    companyPermissionModules.manageClients,
  );

  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);

  const resolvedCompanyId = companyId || location.state?.companyId;
  const fallbackClient = location.state?.clientData;
  const client = current || fallbackClient || null;

  const goToCompanyClientsTab = useCallback(() => {
    if (!resolvedCompanyId) {
      history.push(ROUTE_PATHS.companiesList);
      return;
    }

    history.push(
      adminCompanyClientRoutes.companyDetailsTabLocation(
        resolvedCompanyId,
        COMPANY_DETAILS_CLIENTS_TAB,
      ),
    );
  }, [history, resolvedCompanyId]);

  useEffect(() => {
    if (!id) {
      goToCompanyClientsTab();
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    getAdminCompanyClient(id)
      .then((response) => {
        if (cancelled) return;
        setCurrent(extractAdminPortalData(response));
      })
      .catch((error) => {
        if (cancelled) return;
        setCurrent(null);
        const message =
          error?.response?.data?.message || shared.generalErrMsg;
        showErrorMsg(message);
        goToCompanyClientsTab();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, goToCompanyClientsTab, shared.generalErrMsg]);

  const breadcrumbList = useMemo(
    () => [
      { path: ROUTE_PATHS.companiesList, label: companiesList.listBreadcrumb },
      {
        path: resolvedCompanyId
          ? adminCompanyClientRoutes.companyDetailsClientsTab(resolvedCompanyId)
          : "",
        label: companiesList.clientsTab,
      },
      {
        path: "",
        label: client?.name || "",
      },
    ],
    [companiesList.listBreadcrumb, companiesList.clientsTab, client?.name, id, resolvedCompanyId],
  );

  const onEditProfile = useCallback(() => {
    if (!resolvedCompanyId || !id) return;
    history.push({
      pathname: adminCompanyClientRoutes.edit(resolvedCompanyId, id),
      state: { clientData: client, companyId: resolvedCompanyId },
    });
  }, [history, resolvedCompanyId, id, client]);

  return {
    clients,
    breadcrumbList,
    client,
    loading,
    canEdit,
    eventsPortal: "admin",
    clientId: client?.client_id ?? null,
    onEditProfile,
  };
};

export default useAdminViewClient;
